import { useReducer } from 'react';

const TYPES = {
  // Core
  UPDATE_NAME: 'UPDATE_NAME',
  UPDATE_EMAIL: 'UPDATE_EMAIL',
  UPDATE_LANGUAGE_ID: 'UPDATE_LANGUAGE_ID',

  // Features
  ADD_FEATURE: 'ADD_FEATURE',
  REMOVE_FEATURE: 'REMOVE_FEATURE',

  // Optional
  UPDATE_PHONE: 'UPDATE_PHONE',
  UPDATE_ADDRESS_1: 'UPDATE_ADDRESS_1',
  UPDATE_ADDRESS_2: 'UPDATE_ADDRESS_2',
  UPDATE_CITY: 'UPDATE_CITY',
  UPDATE_REGION: 'UPDATE_REGION',
  UPDATE_POSTCODE: 'UPDATE_POSTCODE',
  UPDATE_COUNTRY: 'UPDATE_COUNTRY',

  // Trusted sender
  UPDATE_CLIENT_TRUSTED_SENDERS: 'UPDATE_CLIENT_TRUSTED_SENDERS'
};

const reducer = (state, { type, ...params }) => {
  switch (type) {
    // Core
    case TYPES.UPDATE_NAME:
      return { ...state, name: params.name };
    case TYPES.UPDATE_EMAIL:
      return { ...state, email: params.email };
    case TYPES.UPDATE_LANGUAGE_ID:
      return { ...state, language_id: params.language_id };

    // Features
    case TYPES.ADD_FEATURE:
      return { ...state, features: state.features.concat(params.feature) };
    case TYPES.REMOVE_FEATURE:
      return {
        ...state,
        features: state.features.filter(feature => feature !== params.feature)
      };

    // Optional
    case TYPES.UPDATE_PHONE:
      return { ...state, phone: params.phone };
    case TYPES.UPDATE_ADDRESS_1:
      return { ...state, address_1: params.address_1 };
    case TYPES.UPDATE_ADDRESS_2:
      return { ...state, address_2: params.address_2 };
    case TYPES.UPDATE_CITY:
      return { ...state, city: params.city };
    case TYPES.UPDATE_REGION:
      return { ...state, region: params.region };
    case TYPES.UPDATE_POSTCODE:
      return { ...state, postcode: params.postcode };
    case TYPES.UPDATE_COUNTRY:
      return { ...state, country: params.country };

    // trusted sender
    case TYPES.UPDATE_CLIENT_TRUSTED_SENDERS:
      return { ...state, client_trusted_senders: params.client_trusted_senders };

    default:
      throw new Error(`No event defined in useClientReducer for ${type}`);
  }
};

const defaultFeatureGates = [
  'auth - sso',
  'authorisation',
  'broadcast',
  'builder',
  'channel - alexa',
  'channel - converse',
  'channel - embedded',
  'channel - facebook messenger',
  'channel - full page',
  'channel - instagram',
  'channel - microsoft teams',
  'channel - rcs',
  'channel - sms',
  'channel - telegram',
  'channel - viber',
  'channel - voip',
  'channel - voice',
  'channel - whatsapp',
  'emails',
  'faq regression testing',
  'feedback',
  'integrations',
  'knowledge bases',
  'languages',
  'live chats',
  'owner area',
  'payments',
  's3 storage',
  'semantic search',
  'small talk',
  'templating',
  'whitelabel footer'
];

const defaultClient = {
  name: '',
  email: '',
  language_id: null,
  features: [],
  feature_gates: defaultFeatureGates,
  phone: '',
  address_1: '',
  address_2: '',
  city: '',
  region: '',
  postcode: '',
  country: null,
  client_trusted_senders: [],
};

const setInitialClient = client => {
  return {
    name: client.name || '',
    email: client.email || '',
    language_id: client.default_language ? client.default_language.id : null,
    features: client.features.map(feature => feature.key),
    phone: client.phone || '',
    address_1: client.address_1 || '',
    address_2: client.address_2 || '',
    city: client.city || '',
    region: client.region || '',
    postcode: client.postcode || '',
    country: client.country || '',
    feature_gates: client.feature_gates || [],
    client_trusted_senders: client.client_trusted_senders || [],
  };
};

const useClient = (initialClient = defaultClient) => {
  const [client, dispatch] = useReducer(
    reducer,
    setInitialClient(initialClient)
  );

  const valid = ['name', 'email', 'language_id'].every(key => {
    return Boolean(client[key]);
  });

  const hasFeature = key => {
    return client.features.includes(key);
  };

  const hasActiveFeatureGate = key => {
    return client.feature_gates.includes(key);
  };

  return {
    client,
    dispatch,
    valid,
    form: client,
    TYPES,
    hasFeature,
    hasActiveFeatureGate
  };
};

export default useClient;
